import { useCallback, useEffect, useState } from 'react';
import { useNavigation } from '../../navigation/NavigationProvider';
import {
  Nami,
  NamiCampaign,
  NamiCampaignManager,
  NamiCampaignRuleType,
} from '@namiml/web-sdk';
import ErrorMessage from '../../components/ErrorMessage';
import { augmentStoredData } from '../../utils/paywalls';
import { dispatchSdkStateChange } from '../../hooks/useSdkInitialized';
import {
  NAMI_PAYWALL_CONTEXT,
  NAMI_SHOW_PAYWALL,
  NAMI_SDK_STATE_KEY,
} from '../../utils/const';
import Loader from '../../components/Loader';

const NOT_INITIALIZED_ERROR = 'SDK has not been initialized.';

interface CampaignListProps {
  onReset: () => void;
}

const CampaignList = ({ onReset }: CampaignListProps) => {
  const navigation = useNavigation();
  const [campaigns, setCampaigns] = useState<NamiCampaign[]>([]);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const loadCampaigns = useCallback(() => {
    try {
      setIsLoading(true);
      const available = NamiCampaignManager.allCampaigns().filter((campaign) => Boolean(campaign.value));
      setCampaigns(available);
      return available;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`Failed to load campaigns: ${message}`);
      if (message.includes(NOT_INITIALIZED_ERROR)) {
        onReset();
        navigation.navigate('/');
      }
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [navigation, onReset]);

  const clearConfiguration = useCallback(() => {
    try {
      localStorage.removeItem(NAMI_PAYWALL_CONTEXT);
      localStorage.removeItem(NAMI_SHOW_PAYWALL);
      localStorage.removeItem(NAMI_SDK_STATE_KEY);
      localStorage.clear();
      dispatchSdkStateChange();
      onReset();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`Failed to clear configuration: ${message}`);
    }
  }, [onReset]);

  const isCampaignAvailable = useCallback((value: string | null | undefined) => {
    try {
      return NamiCampaignManager.isCampaignAvailable(value ?? '');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`Failed to check campaign availability: ${message}`);
      return false;
    }
  }, []);

  const openPaywall = useCallback(
    (campaign: NamiCampaign, shouldShowPaywall: boolean) => {
      if (!shouldShowPaywall) {
        localStorage.removeItem(NAMI_SHOW_PAYWALL);
      }

      const available = isCampaignAvailable(campaign.value);
      if (!available) {
        setError('Selected campaign is not available.');
        return;
      }

      const params = new URLSearchParams();
      const isUrlCampaign = campaign.type === NamiCampaignRuleType.URL || campaign.type === 'url';
      if (isUrlCampaign) {
        params.set('url', campaign.value ?? '');
      } else {
        params.set('label', campaign.value ?? '');
      }
      params.set('showPaywall', String(shouldShowPaywall));

      navigation.navigate(`/paywall?${params.toString()}`);
    },
    [isCampaignAvailable, navigation]
  );

  const refreshCampaigns = useCallback(async () => {
    try {
      setIsLoading(true);
      const refreshed = await NamiCampaignManager.refresh();
      setCampaigns(refreshed);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(`Failed to refresh campaigns: ${message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    augmentStoredData();
    loadCampaigns();
    const unsubscribe = NamiCampaignManager.registerAvailableCampaignsHandler(
      (updatedCampaigns: NamiCampaign[]) => {
        augmentStoredData();
        setCampaigns(updatedCampaigns.filter((campaign) => Boolean(campaign.value)));
      }
    );

    return () => {
      unsubscribe?.();
    };
  }, [loadCampaigns]);

  return (
    <div className="panel stack">
      {isLoading && <Loader />}
      <div className="list-panel">
        <h1 className="panel-title">Campaigns</h1>
        <p className="panel-subtitle">
          Use the remote arrows to focus a campaign and press OK to launch the paywall.
        </p>
        <p className="panel-subtitle">SDK Version: {Nami.sdkPackageVersion()}</p>
        {error && <ErrorMessage message={error} />}
        <div className="inline-actions">
          <button 
            type="button" 
            className="tv-button" 
            onClick={refreshCampaigns} 
            onKeyDown={(e) => {
              if (e.keyCode === 142 || e.keyCode === 195) {
                e.preventDefault();
                refreshCampaigns();
              }
            }}
          >
            Refresh Campaigns
          </button>
          <button
            type="button"
            className="tv-button"
            data-variant="danger"
            onClick={clearConfiguration}
            onKeyDown={(e) => {
              if (e.keyCode === 142 || e.keyCode === 195) {
                e.preventDefault();
                clearConfiguration();
              }
            }}
          >
            Clear Configuration
          </button>
        </div>
      </div>

      <div className="panel list-panel">
        <div className="list-row" style={{ fontWeight: 600, letterSpacing: '0.05em' }}>
          <span>Name</span>
          <span>Value</span>
          <span style={{ textAlign: 'right' }}>Actions</span>
        </div>
        <div className="section-divider" />
        {campaigns.length === 0 && (
          <div className="panel-subtitle">No campaigns available</div>
        )}
        {campaigns.map((campaign) => (
          <div key={campaign.rule} className="list-row">
            <span>{campaign.name}</span>
            <span>{campaign.value}</span>
            <div className="inline-actions" style={{ justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="tv-button"
                id={`placement-row-${campaign.value}`}
                data-testid={`placement-row-${campaign.value}`}
                onClick={() => openPaywall(campaign, true)}
                onKeyDown={(e) => {
                  if (e.keyCode === 142 || e.keyCode === 195) {
                    e.preventDefault();
                    openPaywall(campaign, true);
                  }
                }}
              >
                Launch
              </button>
              <button
                type="button"
                className="tv-button"
                data-variant="ghost"
                id={`placement-context-${campaign.value}`}
                data-testid={`placement-context-${campaign.value}`}
                onClick={() => openPaywall(campaign, false)}
                onKeyDown={(e) => {
                  if (e.keyCode === 142 || e.keyCode === 195) {
                    e.preventDefault();
                    openPaywall(campaign, false);
                  }
                }}
              >
                Launch with Context
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CampaignList;
